import { computed } from '@vue/composition-api'
import { required, requiredIf, minLength, maxLength, minValue, maxValue, email, alphaNum, not, sameAs } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import { notAllowedChars, step, notIn, sumOfPercentOwnership } from '@/utils/validators'
import { DEFAULT_ERROR_MESSAGES } from '@/constants'

const FAIL_AUTOMATICALLY_VALIDATOR = () => () => false
// const REGEX_VALIDATOR = regex => value => new RegExp(regex).test(value)

const stepValidator = (params) => ({
  $validator: step(params),
  $message: DEFAULT_ERROR_MESSAGES.step(params),
  $params: { step: params },
})

const notAllowedCharsValidator = params => ({
  $validator: notAllowedChars(params),
  $message: DEFAULT_ERROR_MESSAGES.notAllowedChars(params),
  $params: { characters: params },
})

const FIELDS_WITH_INTERNAL_VALIDATORS = [
  'effectiveStartDate',
]

export default function useFieldValidations ({ id, fieldValue, field }) {
  const hasNestedQuestions = field.value.hasOwnProperty('questions') || field.value.config?.hasOwnProperty('fields')
  const rules = getValidationRules(field.value)
  const hasActions = field.value.actions?.length > 0

  // NOTE: Return early to prevent using Vuelidate
  //       without any rules
  //       Need to check if field has actions in case actions
  //       do add a validation rule
  if (!hasNestedQuestions && !hasActions &&
    Object.keys(rules).length === 0 &&
    !FIELDS_WITH_INTERNAL_VALIDATORS.includes(field.value.questionId)
  ) {
    return {
      $v: {
        $errors: [],
        $touch: () => {},
        $invalid: false,
        $anyDirty: false,
      },
    }
  }

  // NOTE: We are technically evaluating this function twice
  //       but that’s still more preferabble than creating a
  //       computed property even if we never actually need it
  //       to be reactive
  const rulesRef = computed(() => getValidationRules(field.value))
  const $v = useVuelidate(
    { [id]: rulesRef },
    { [id]: fieldValue }
  )

  return {
    $v,
  }
}

function resolveValidator (validatorName, params, message) {
  switch (validatorName) {
    case 'requiredIf':
      return { requiredIf: requiredIf(params) }
    case 'minLength':
      return { minLength: minLength(params) }
    case 'maxLength':
      return { maxLength: maxLength(params) }
    case 'notAllowedChars':
      return { notAllowedChars: notAllowedCharsValidator(params) }
    case 'minValue':
      return { minValue: minValue(params) }
    case 'maxValue':
      return { maxValue: maxValue(params) }
    case 'email':
      return { email }
    case 'step':
      return { step: stepValidator(params) }
    case 'alphaNum':
      return { alphaNum }
    case 'alwaysFail':
      return {
        alwaysFail: {
          $validator: FAIL_AUTOMATICALLY_VALIDATOR(params),
          $message: message,
          $params: params,
        },
      }
    case 'notSameAs':
      return {
        notSameAs: {
          $validator: not(sameAs(params)).$validator,
          $message: message,
          $params: params,
        },
      }
    case 'notIn':
      return {
        notIn: {
          $validator: notIn(params),
          $message: message,
          $params: params,
        },
      }
      // TODO: For future reference when we need regex validators
      // case 'regex':
      //   return {
      //     // TODO: This name will likely need to be dynamic
      //     regex: {
      //       $validator: REGEX_VALIDATOR(params),
      //       $message: message,
      //       $params: params,
      //     },
      //   }
    case 'sumOfPercentOwnership':
      return {
        sumOfPercentOwnership: {
          $validator: sumOfPercentOwnership(params),
          $message: 'Sum of percent ownership should be 100',
          $params: params,
        },
      }
    default:
      console.warn(`Validator ${validatorName} does not exist!`)
  }
}

function getValidationRules (field) {
  let validators = {}
  const fieldConfig = field.config

  if (field.required) {
    const requiredValidator = {
      $message: `${field.label || 'Field'} is required`,
      $validator: required.$validator,
    }
    validators.required = requiredValidator
  }

  if (fieldConfig?.validations) {
    const validatorNames = Object.keys(fieldConfig.validations)

    validators = validatorNames.reduce((allValidators, validatorName) => ({
      ...allValidators,
      ...resolveValidator(validatorName, fieldConfig.validations[validatorName]),
    }), validators)
  }

  if (field.customValidations) {
    field.customValidations.forEach(customValidator => {
      const validator = resolveValidator(customValidator.type, customValidator.params, customValidator.message)

      validators[customValidator.type] = validator[customValidator.type]
    })
  }

  return validators
}
